import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Briefcase, Users, Receipt, Rocket, KeyRound, LogOut, Shield, DollarSign, Target, Tag, FileText, MessagesSquare, Monitor, Trash2, ExternalLink, TrendingUp } from "lucide-react";
import { MarginsPanel } from "@/components/admin/managed/MarginsPanel";
import { AdminNav, Breadcrumb, ALL_ITEMS } from "@/components/admin/AdminNav";
import { HeatmapPanel } from "@/components/admin/heatmap/HeatmapPanel";
import { ApifyUsageCard } from "@/components/admin/ApifyUsageCard";
import { PerplexityUsageCard } from "@/components/admin/PerplexityUsageCard";
import { GooglePlacesUsageCard } from "@/components/admin/GooglePlacesUsageCard";
import { OpenRouterTest } from "@/components/admin/OpenRouterTest";
import { OpenRouterCredits } from "@/components/admin/OpenRouterCredits";
import { AiUsageChart } from "@/components/admin/AiUsageChart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SwissCross } from "@/components/Header";
import { adminApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/constants/testIds";
import { LeadGenPanel } from "@/components/admin/LeadGenPanel";
import { Credits } from "@/components/SiteFooter";
import { MetaCampaignActions } from "@/components/admin/MetaCampaignActions";
import { TransactionsPanel } from "@/components/admin/managed/TransactionsPanel";
import { ManagedCampaignsPanel } from "@/components/admin/managed/ManagedCampaignsPanel";
import { DiscountCodesPanel } from "@/components/admin/managed/DiscountCodesPanel";
import { CatalogAdmin } from "@/components/admin/managed/CatalogAdmin";
import { SocialPilotPanel } from "@/components/admin/SocialPilotPanel";
import { AdvWebPanel } from "@/components/admin/AdvWebPanel";
import { VisitorsPanel } from "@/components/admin/VisitorsPanel";
import { AnalysesHistoryPanel } from "@/components/admin/AnalysesHistoryPanel";
import { ChatbotStatsPanel } from "@/components/admin/ChatbotStatsPanel";
import { InvoicesPanel } from "@/components/admin/managed/InvoicesPanel";
import { AdminChatPanel } from "@/components/chat/AdminChatPanel";

const DashboardShortcuts = ({ onPick, chat, addons }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
    {[["2.1", "Lead Gen & scraping", Target], ["5.3", "Stato campagne", Briefcase, addons ? `${addons} servizi da attivare` : ""],
      ["8.2", "Comunicazioni", MessagesSquare, chat ? `${chat} non letti` : ""], ["12.3", "Marginalità", TrendingUp]].map(([n, label, Icon, note]) => (
      <button key={n} data-testid={`dash-shortcut-${n}`} onClick={() => onPick(n)}
        className="text-left border border-[#d5dbde] bg-white p-5 hover:border-[#E0262C] hover:-translate-y-0.5 transition-[transform,border-color]">
        <Icon className="w-5 h-5 text-[#E0262C] mb-3" />
        <div className="font-mono text-[10px] text-[#9ca3af]">{n}</div>
        <div className="font-display text-lg">{label}</div>
        {note && <div className="text-xs text-[#E0262C] mt-1">{note}</div>}
      </button>
    ))}
  </div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const firstFor = (t) => (ALL_ITEMS.find((i) => i.tab === t) || ALL_ITEMS[0]).n;
  const [nav, setNav] = useState(() => firstFor(params.get("tab") || "dashboard"));
  const tab = ALL_ITEMS.find((i) => i.n === nav)?.tab || "dashboard";
  const setTab = (t) => setNav(firstFor(t));
  const [addonsPending, setAddonsPending] = useState(0);
  useEffect(() => {
    const load = () => adminApi.addonsPending().then((d) => setAddonsPending(d.campaigns)).catch(() => {});
    load();
    window.addEventListener("addons-changed", load);
    return () => window.removeEventListener("addons-changed", load);
  }, [nav]);
  const [chatUser, setChatUser] = useState(params.get("user"));
  const [chatUnread, setChatUnread] = useState(0);
  const [leadTarget, setLeadTarget] = useState(null);
  useEffect(() => {
    const h = (e) => { setLeadTarget({ ...e.detail, at: Date.now() }); setTab("leadgen"); window.scrollTo({ top: 0 }); };
    window.addEventListener("open-leadgen", h);
    return () => window.removeEventListener("open-leadgen", h);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const load = () => adminApi.chatThreads().then((d) => setChatUnread(d.unread_total)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);
  const openChat = (uid) => { setChatUser(uid); setTab("chat"); };
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({});

  useEffect(() => {
    adminApi.stats().then(setStats).catch(() => {});
  }, []);

  useEffect(() => {
    if (["dashboard", "heatmap", "leadgen", "transactions", "managed", "discounts", "invoices", "margins", "chat"].includes(tab)) return; // managed internally
    const fn = { users: adminApi.users, orders: adminApi.orders, campaigns: adminApi.campaigns, keys: adminApi.getKeys }[tab];
    if (!fn) return;
    fn().then((d) => setData((prev) => ({ ...prev, [tab]: d }))).catch(() => {});
  }, [tab]);

  return (
    <div className="min-h-screen bg-[#E6EAEC] text-[#2B3036]">
      {/* Header */}
      <header className="border-b border-[#d5dbde] sticky top-0 z-30 bg-[#E6EAEC]/95 backdrop-blur">
        <div className="px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/brand/aurora-mark.webp" alt="AURORA-ADV logo · agenzia marketing digitale Ticino" className="w-9 h-9 rounded-md object-cover" />
            <div>
              <div className="font-display text-lg leading-none">Aurora<span className="aurora-text">-ADV</span></div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-[#6b7280] font-mono mt-1 flex items-center gap-1.5">
                <Shield className="w-2.5 h-2.5 text-[#10b981]" /> Super Admin Console
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#5b6168] font-mono hidden md:inline">{user?.email}</span>
            <Button data-testid={TID.logoutBtn} onClick={logout} variant="outline" size="sm"
              className="bg-transparent border-[#d5dbde] text-[#2B3036] hover:bg-[#f4f6f7] hover:text-[#E0262C] rounded-sm">
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row">
        <AdminNav current={nav} onPick={(it) => { setNav(it.n); window.scrollTo({ top: 0 }); }} badges={{ chat: chatUnread, addons: addonsPending }} />
        <main className="flex-1 min-w-0 px-6 lg:px-10 py-8">
        <Breadcrumb current={nav} />
        {tab === "dashboard" && (
          <div data-testid={TID.adminStats} className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#d5dbde] mb-10 divide-x divide-[#d5dbde]">
            <StatCard label="Utenti" value={stats?.users ?? "—"} icon={Users} />
            <StatCard label="Ordini" value={stats?.orders ?? "—"} icon={Receipt} />
            <StatCard label="Campagne" value={stats?.campaigns ?? "—"} icon={Rocket} />
            <StatCard label="Revenue" value={stats ? `€ ${stats.revenue_eur.toFixed(0)}` : "—"} icon={DollarSign} aurora />
          </div>
        )}
        {tab === "dashboard" && <DashboardShortcuts onPick={setNav} chat={chatUnread} addons={addonsPending} />}
        {tab === "heatmap" && <HeatmapPanel />}

        {/* Content */}
        {tab === "transactions" && <TransactionsPanel />}
        {tab === "managed" && <ManagedCampaignsPanel />}
        {tab === "discounts" && <DiscountCodesPanel />}
        {tab === "catalog" && <CatalogAdmin />}
        {tab === "socialpilot" && <SocialPilotPanel />}
        {tab === "advweb" && <AdvWebPanel />}
        {tab === "visitors" && <VisitorsPanel />}
        {tab === "analyses" && <AnalysesHistoryPanel />}
        {tab === "chatbotstats" && <ChatbotStatsPanel />}
        {tab === "invoices" && <InvoicesPanel />}
        {tab === "margins" && <MarginsPanel />}
        {tab === "users" && <UsersTable users={data.users?.users || []} onChat={openChat}
          onDeleted={() => adminApi.users().then((d) => setData((p) => ({ ...p, users: d })))} />}
        {tab === "chat" && <AdminChatPanel initialUser={chatUser} />}
        {tab === "orders" && <OrdersTable orders={data.orders?.orders || []} />}
        {tab === "campaigns" && <CampaignsTable campaigns={data.campaigns?.campaigns || []}
          onDeleted={() => adminApi.campaigns().then((d) => setData((p) => ({ ...p, campaigns: d })))} />}
        {tab === "leadgen" && <LeadGenPanel target={leadTarget} />}
        {tab === "keys" && <ApiKeysPanel initial={data.keys || {}} />}
        </main>
      </div>

      <footer className="border-t border-[#d5dbde] mt-10 py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-3 text-xs text-[#6b7280] font-mono">
          <div className="flex items-center gap-2"><SwissCross className="w-3 h-3" /> Aurora-ADV · Swiss Made · PIXAWEB Sagl</div>
          <Credits dark />
        </div>
      </footer>
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, aurora }) => (
  <div className="p-6 bg-white">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#6b7280] font-mono mb-3">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className={`font-display text-4xl leading-none ${aurora ? "aurora-text" : ""}`}>{value}</div>
  </div>
);

const Device = ({ m, testid }) => !m ? <span className="text-[#52525b]">—</span> : (
  <div data-testid={testid} className="space-y-0.5">
    <div className="inline-flex items-center gap-1 text-[#2B3036]"><Monitor className="w-3 h-3 text-[#6b7280]" />{m.os} · {m.browser}{m.mobile ? " · mobile" : ""}</div>
    <div className="text-[#6b7280]">IP {m.ip} · {(m.at || "").slice(0, 16).replace("T", " ")}</div>
  </div>
);

const deleteUsers = async (ids, onDeleted, clear) => {
  if (!ids.length || !window.confirm(`Eliminare definitivamente ${ids.length} utent${ids.length === 1 ? "e" : "i"}? Verranno cancellati anche chat e notifiche.`)) return;
  try {
    const r = await adminApi.deleteUsers(ids);
    if (r.deleted.length) toast.success(`${r.deleted.length} utent${r.deleted.length === 1 ? "e eliminato" : "i eliminati"}`);
    if (r.blocked.length) toast.error(`${r.blocked.length} non eliminabil${r.blocked.length === 1 ? "e" : "i"}: ${r.blocked[0].reason}`);
    clear();
    onDeleted();
  } catch { toast.error("Errore eliminazione"); }
};

const UsersTable = ({ users, onChat, onDeleted }) => {
  const [sel, setSel] = useState([]);
  const clients = users.filter((u) => u.role !== "super_admin");
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
  <div className="space-y-3">
  <div className="flex items-center gap-3 min-h-[36px]">
    <span data-testid="users-selected-count" className="text-xs font-mono text-[#6b7280]">{sel.length ? `${sel.length} selezionati` : `${clients.length} clienti`}</span>
    {sel.length > 0 && (
      <button data-testid="users-delete-selected" onClick={() => deleteUsers(sel, onDeleted, () => setSel([]))}
        className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium bg-[#ef4444] text-white hover:bg-[#dc2626]">
        <Trash2 className="w-3.5 h-3.5" /> Elimina selezionati
      </button>
    )}
  </div>
  <div data-testid={TID.adminUsers} className="border border-[#d5dbde] overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-widest text-[#6b7280] font-mono bg-[#f4f6f7]">
          <th className="px-4 py-3"><input data-testid="users-select-all" type="checkbox" className="accent-[#E0262C]"
            checked={clients.length > 0 && sel.length === clients.length} onChange={(e) => setSel(e.target.checked ? clients.map((u) => u.user_id) : [])} /></th>
          <th className="px-4 py-3">Nome</th>
          <th className="px-4 py-3">Email</th>
          <th className="px-4 py-3">Cellulare</th>
          <th className="px-4 py-3">Iscrizione (IP · sistema)</th>
          <th className="px-4 py-3">Ultimo accesso</th>
          <th className="px-4 py-3">Ruolo</th>
          <th className="px-4 py-3">Provider</th>
          <th className="px-4 py-3">Creato</th>
          <th className="px-4 py-3">Chat</th>
        </tr>
      </thead>
      <tbody>
        {users.map((u) => (
          <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-t border-[#d5dbde] hover:bg-[#f4f6f7]">
            <td className="px-4 py-3">{u.role !== "super_admin" && <input data-testid={`user-select-${u.user_id}`} type="checkbox" className="accent-[#E0262C]" checked={sel.includes(u.user_id)} onChange={() => toggle(u.user_id)} />}</td>
            <td className="px-4 py-3">{u.name}</td>
            <td className="px-4 py-3 font-mono text-xs"><a href={`mailto:${u.email}`} className="hover:text-[#E0262C]">{u.email}</a></td>
            <td className="px-4 py-3 font-mono text-xs">{u.phone ? <a data-testid={`user-phone-${u.user_id}`} href={`tel:${u.phone}`} className="hover:text-[#E0262C]">{u.phone}</a> : <span className="text-[#52525b]">—</span>}</td>
            <td className="px-4 py-3 font-mono text-[11px]"><Device m={u.signup_meta} testid={`user-signup-${u.user_id}`} /></td>
            <td className="px-4 py-3 font-mono text-[11px]"><Device m={u.last_login_meta} testid={`user-last-${u.user_id}`} /></td>
            <td className="px-4 py-3">
              <span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${u.role === "super_admin" ? "bg-[#E0262C] text-white" : "bg-[#e4e7e9] text-[#5b6168]"}`}>
                {u.role}
              </span>
            </td>
            <td className="px-4 py-3 text-[#6b7280] text-xs font-mono">{u.provider}</td>
            <td className="px-4 py-3 text-[#6b7280] text-xs font-mono">{(u.created_at || "").slice(0, 19).replace("T", " ")}</td>
            <td className="px-4 py-3">{u.role !== "super_admin" && (
              <button data-testid={`user-chat-${u.user_id}`} onClick={() => onChat(u.user_id)}
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase border border-[#d5dbde] hover:border-[#E0262C] hover:text-[#E0262C] px-2 py-1">
                <MessagesSquare className="w-3 h-3" /> Scrivi
              </button>)}
              {u.role !== "super_admin" && (
              <button data-testid={`user-delete-${u.user_id}`} onClick={() => deleteUsers([u.user_id], onDeleted, () => setSel(sel.filter((x) => x !== u.user_id)))} title="Elimina utente"
                className="ml-1.5 inline-flex items-center border border-[#d5dbde] hover:border-[#ef4444] hover:text-[#ef4444] px-2 py-1"><Trash2 className="w-3 h-3" /></button>)}
            </td>
          </tr>
        ))}
        {!users.length && (
          <tr><td colSpan={11} className="px-4 py-10 text-center text-[#6b7280] font-mono text-xs">Nessun utente</td></tr>
        )}
      </tbody>
    </table>
  </div>
  </div>
  );
};

const OrdersTable = ({ orders }) => (
  <div data-testid={TID.adminOrders} className="border border-[#d5dbde] overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-widest text-[#6b7280] font-mono bg-[#f4f6f7]">
          <th className="px-4 py-3">Order ID</th>
          <th className="px-4 py-3">Cliente</th>
          <th className="px-4 py-3">Landing</th>
          <th className="px-4 py-3">Durata</th>
          <th className="px-4 py-3">Importo</th>
          <th className="px-4 py-3">Status</th>
          <th className="px-4 py-3">Creato</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.order_id} className="border-t border-[#d5dbde] hover:bg-[#f4f6f7]">
            <td className="px-4 py-3 font-mono text-xs">{o.order_id}</td>
            <td className="px-4 py-3 font-mono text-xs">{o.user_email}</td>
            <td className="px-4 py-3 font-mono text-xs truncate max-w-[200px]">{o.campaign?.landing_url}</td>
            <td className="px-4 py-3 font-mono text-xs">{o.campaign?.duration_days}gg × €{o.campaign?.daily_budget}</td>
            <td className="px-4 py-3 font-display text-base aurora-text">€ {o.amount.toFixed(0)}</td>
            <td className="px-4 py-3">
              <span className="text-[10px] font-mono uppercase bg-[#10b981] text-white px-2 py-0.5">{o.status}</span>
            </td>
            <td className="px-4 py-3 text-[#6b7280] text-xs font-mono">{(o.created_at || "").slice(0, 19).replace("T", " ")}</td>
          </tr>
        ))}
        {!orders.length && (
          <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6b7280] font-mono text-xs">Nessun ordine pagato</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const deleteCampaign = async (c, onDeleted) => {
  if (!window.confirm(`Eliminare definitivamente la campagna ${c.id.slice(0, 8)} (${c.params?.skill || c.landing_url})? Verranno cancellati anche ordini, fatture, pagamenti e lead collegati.`)) return;
  try {
    await adminApi.deleteCampaigns([c.id]);
    toast.success("Campagna eliminata");
    onDeleted();
  } catch { toast.error("Errore eliminazione"); }
};

const CampaignsTable = ({ campaigns, onDeleted }) => (
  <div data-testid={TID.adminCampaigns} className="border border-[#d5dbde] overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-widest text-[#6b7280] font-mono bg-[#f4f6f7]">
          <th className="px-4 py-3">ID</th>
          <th className="px-4 py-3">Landing</th>
          <th className="px-4 py-3">Geo</th>
          <th className="px-4 py-3">Status</th>
          <th className="px-4 py-3">Budget</th>
          <th className="px-4 py-3">Creato</th>
          <th className="px-4 py-3">Meta Ads</th>
          <th className="px-4 py-3"></th>
        </tr>
      </thead>
      <tbody>
        {campaigns.map((c) => (
          <tr key={c.id} className="border-t border-[#d5dbde] hover:bg-[#f4f6f7]">
            <td className="px-4 py-3 font-mono text-xs">{c.id.slice(0, 8)}</td>
            <td className="px-4 py-3 font-mono text-xs truncate max-w-[200px]">{c.landing_url}</td>
            <td className="px-4 py-3 text-xs">{c.targeting?.geography}</td>
            <td className="px-4 py-3">
              <span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${
                c.status === "active" ? "bg-[#10b981] text-white" :
                c.status === "warming_up" ? "bg-[#f59e0b] text-white" :
                c.status === "completed" ? "bg-[#e4e7e9] text-[#5b6168]" :
                "bg-[#e4e7e9] text-[#5b6168]"
              }`}>{c.status}</span>
            </td>
            <td className="px-4 py-3 font-mono text-xs">€ {c.total_budget?.toFixed(0)}</td>
            <td className="px-4 py-3 text-[#6b7280] text-xs font-mono">{(c.created_at || "").slice(0, 19).replace("T", " ")}</td>
            <td className="px-4 py-3"><MetaCampaignActions campaign={c} /></td>
            <td className="px-4 py-3">
              <button data-testid={`campaign-delete-${c.id}`} onClick={() => deleteCampaign(c, onDeleted)} title="Elimina campagna"
                className="p-1.5 text-[#6b7280] hover:text-[#E0262C] transition-colors"><Trash2 className="w-4 h-4" /></button>
            </td>
          </tr>
        ))}
        {!campaigns.length && (
          <tr><td colSpan={8} className="px-4 py-10 text-center text-[#6b7280] font-mono text-xs">Nessuna campagna</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const KEYS = [
  { id: "openrouter_api_key", billing: "https://openrouter.ai/settings/credits", label: "API OPEN ROUTER — audit SEO, chatbot, Social Pilot, ADV Web", hint: "openrouter.ai → Keys → Create Key" },
  { id: "meta_access_token", billing: "https://business.facebook.com/billing_hub", label: "Meta Marketing API — Access Token", hint: "Business Manager → System User → Generate Token" },
  { id: "google_ads_developer_token", billing: "https://ads.google.com/aw/billing/summary", label: "Google Ads — Developer Token", hint: "ads.google.com → API Center" },
  { id: "meta_ad_account_id", label: "Meta — Ad Account ID", hint: "es. act_1234567890 (Business Manager → Account pubblicitari)" },
  { id: "meta_page_id", label: "Meta — Facebook Page ID", hint: "Pagina → Informazioni → ID pagina" },
  { id: "meta_app_secret", label: "Meta — App Secret (firma webhook)", hint: "developers.facebook.com → App → Impostazioni → Base" },
  { id: "whatsapp_cloud_token", billing: "https://business.facebook.com/billing_hub", label: "WhatsApp Business Cloud — Access Token", hint: "developers.facebook.com → WhatsApp" },
  { id: "whatsapp_phone_number_id", label: "WhatsApp — Phone Number ID", hint: "WhatsApp → Configurazione API → Phone number ID" },
  { id: "whatsapp_verify_token", label: "WhatsApp — Webhook Verify Token", hint: "Una parola segreta a tua scelta, da ripetere su Meta" },
  { id: "linkedin_access_token", billing: "https://www.linkedin.com/campaignmanager/", label: "LinkedIn Marketing — Access Token", hint: "linkedin.com/developers" },
  { id: "google_places_api_key", billing: "https://console.cloud.google.com/billing", label: "Google Places API (New) — API Key", hint: "console.cloud.google.com → API e servizi → Credenziali" },
  { id: "hunter_api_key", billing: "https://hunter.io/subscriptions", label: "Hunter.io — API Key (ricerca + verifica email)", hint: "hunter.io → Dashboard → API" },
  { id: "apollo_api_key", billing: "https://app.apollo.io/#/settings/plans/upgrade", label: "Apollo.io — API Key (arricchimento aziende)", hint: "app.apollo.io → Settings → Integrations → API" },
  { id: "perplexity_api_key", billing: "https://www.perplexity.ai/account/api/billing", label: "Perplexity — API Key (responsabili / decision maker)", hint: "console.perplexity.ai → API Keys" },
  { id: "apify_api_token", billing: "https://console.apify.com/billing", label: "Apify — API Token (profili Instagram e TikTok)", hint: "console.apify.com → Settings → API & Integrations" },
];

const ApiKeysPanel = ({ initial }) => {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(values).filter(([_, v]) => v));
      if (!Object.keys(payload).length) return toast.info("Nessuna modifica");
      await adminApi.setKeys(payload);
      toast.success("API keys salvate");
      setValues({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={TID.adminApiKeys} className="border border-[#d5dbde] p-8">
      <div className="mb-6">
        <h2 className="font-display text-2xl mb-2">Credenziali campagne reali</h2>
        <p className="text-sm text-[#5b6168] max-w-2xl">
          Inserisci le API key dei provider pubblicitari per abilitare le campagne reali. Vuote = modalità simulazione (default). Le chiavi sono salvate server-side e mai esposte in chiaro.
        </p>
      </div>

      <WebhookInfo />
      <div className="space-y-5 max-w-3xl">
        {KEYS.map((k) => (
          <div key={k.id}>
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <label className="text-sm font-medium">{k.label}</label>
              <div className="flex items-baseline gap-3 shrink-0">
                {k.billing && (
                  <a data-testid={`billing-link-${k.id}`} href={k.billing} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-[#E3242B] hover:underline">
                    Ricarica credito <ExternalLink size={11} />
                  </a>
                )}
                <span className="text-[10px] font-mono text-[#6b7280]">
                  {initial[k.id] ? `attuale: ${initial[k.id]}` : "non configurata"}
                </span>
              </div>
            </div>
            <Input
              data-testid={`api-key-${k.id}`}
              type="password"
              placeholder={k.hint}
              value={values[k.id] || ""}
              onChange={(e) => setValues({ ...values, [k.id]: e.target.value })}
              className="h-11 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] placeholder:text-[#52525b] rounded-sm font-mono text-xs"
            />
            {k.id === "openrouter_api_key" && <><OpenRouterTest /><OpenRouterCredits /><AiUsageChart /></>}
            {k.id === "apify_api_token" && <ApifyUsageCard configured={!!initial[k.id]} />}
            {k.id === "perplexity_api_key" && <PerplexityUsageCard configured={!!initial[k.id]} />}
            {k.id === "google_places_api_key" && <GooglePlacesUsageCard configured={!!initial[k.id]} />}
          </div>
        ))}

        <div className="pt-4 border-t border-[#d5dbde] flex items-center justify-between">
          <ModeIndicator />
          <Button data-testid={TID.saveKeysBtn} onClick={save} disabled={saving}
            className="aurora-gradient hover:opacity-90 text-white rounded-sm">
            {saving ? "Salvo..." : "Salva chiavi"}
          </Button>
        </div>
      </div>
    </div>
  );
};

const useMetaStatus = () => {
  const [st, setSt] = useState(null);
  useEffect(() => { adminApi.metaStatus().then(setSt).catch(() => {}); }, []);
  return st;
};

const ModeIndicator = () => {
  const st = useMetaStatus();
  const item = (ok, label) => (
    <span className={ok ? "text-[#10b981]" : "text-[#f59e0b]"}>{label}: {ok ? "REALE" : "SIMULAZIONE"}</span>
  );
  return (
    <div data-testid="api-mode-indicator" className="text-xs font-mono flex gap-4">
      {item(st?.meta_live, "Meta Ads")}{item(st?.whatsapp_live, "WhatsApp")}
    </div>
  );
};

const WebhookInfo = () => {
  const st = useMetaStatus();
  if (!st) return null;
  return (
    <div data-testid="webhook-info" className="max-w-3xl mb-6 border border-[#d5dbde] bg-white p-4 text-xs space-y-1.5">
      <div className="text-[10px] uppercase tracking-widest text-[#6b7280] font-mono">Webhook WhatsApp (da incollare su Meta → WhatsApp → Configurazione)</div>
      <div className="font-mono text-[#2B3036] break-all" data-testid="webhook-url">{st.webhook_url}</div>
      <div className="text-[#6b7280]">Campo da sottoscrivere: <span className="font-mono text-[#2B3036]">messages</span> · Verify token: {st.verify_token_set ? "impostato" : "da impostare qui sotto"} · App secret: {st.app_secret_set ? "impostato" : "consigliato"}</div>
    </div>
  );
};
